/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Yakov Gushchin
 * @version 1.20.1
 * @since 1.14.0
 */

import { createSelector, OutputSelector } from 'reselect';

import { IRootReducer } from '../../reducers/rootReducer';
import { IWizardAdditionalAccountsValidation } from './types/wizardAdditionalAccountsTypes';
import { initWizardAdditionalAccountsState } from './wizardAdditionalAccountsConstant';
import {
    IWizardAdditionalAccountsOrderResult,
    IWizardAdditionalAccountsReducer,
    IWizardAdditionalAccountsSelectedTypes,
    IWizardAdditionalAccountType,
} from './wizardAdditionalAccountsReducer';

export const makeSelectWizardAdditionalAccountsState = (state: IRootReducer): IWizardAdditionalAccountsReducer =>
    state.wizardAdditionalAccounts || initWizardAdditionalAccountsState;

export const makeSelectWizardAdditionalAccountsValidation = ():
    OutputSelector<IRootReducer, IWizardAdditionalAccountsValidation,
        (state: IWizardAdditionalAccountsReducer) => IWizardAdditionalAccountsValidation> =>
    createSelector(makeSelectWizardAdditionalAccountsState, state => state.validation);

export const makeSelectWizardAdditionalAccountsOfferLink = ():
    OutputSelector<IRootReducer, string, (state: IWizardAdditionalAccountsReducer) => string> =>
    createSelector(makeSelectWizardAdditionalAccountsState, state => state.offerLink);

export const makeSelectWizardAdditionalAccountsActivateTypes = ():
    OutputSelector<IRootReducer, IWizardAdditionalAccountType[],
        (state: IWizardAdditionalAccountsReducer) => IWizardAdditionalAccountType[]> =>
    createSelector(makeSelectWizardAdditionalAccountsState, state => state.activateTypes);

export const makeSelectWizardAdditionalAccountsDeactivateTypes = ():
    OutputSelector<IRootReducer, IWizardAdditionalAccountType[],
        (state: IWizardAdditionalAccountsReducer) => IWizardAdditionalAccountType[]> =>
    createSelector(makeSelectWizardAdditionalAccountsState, state => state.deactivateTypes);

export const makeSelectWizardAdditionalAccountsSelectedTypes = ():
    OutputSelector<IRootReducer, IWizardAdditionalAccountsSelectedTypes,
        (state: IWizardAdditionalAccountsReducer) => (IWizardAdditionalAccountsSelectedTypes)> =>
    createSelector(makeSelectWizardAdditionalAccountsState, state => state.selected);

export const makeSelectWizardAdditionalAccountsIsAgreementCheckboxEnabled = ():
    OutputSelector<IRootReducer, boolean,
        (state: IWizardAdditionalAccountsReducer) => boolean> =>
    createSelector(makeSelectWizardAdditionalAccountsState, state => state.isAgreementCheckboxEnabled);

export const makeSelectWizardAdditionalAccountsOrderResult = ():
    OutputSelector<IRootReducer, IWizardAdditionalAccountsOrderResult,
        (state: IWizardAdditionalAccountsReducer) => (IWizardAdditionalAccountsOrderResult)> =>
    createSelector(makeSelectWizardAdditionalAccountsState, state => state.orderResult);

export const makeSelectWizardAdditionalAccountsSubscriberIds = ():
    OutputSelector<IRootReducer, number[],
        (state: IWizardAdditionalAccountsReducer) => (number[])> =>
    createSelector(makeSelectWizardAdditionalAccountsState, state => state.subscriberIds);

export const makeSelectWizardAdditionalAccountsIsOnlineCheckProceeded = ():
    OutputSelector<IRootReducer, boolean,
        (state: IWizardAdditionalAccountsReducer) => boolean> =>
    createSelector(makeSelectWizardAdditionalAccountsState, state => state.isOnlineCheckProceeded);

export const makeSelectWizardAdditionalAccountsRemovedSubscribers = ():
    OutputSelector<IRootReducer, number[],
        (state: IWizardAdditionalAccountsReducer) => number[]> =>
    createSelector(makeSelectWizardAdditionalAccountsState, state => state.removedSubscribers);
